import React, { useState } from 'react';
import { styled as muiStyled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Text } from '@eo-locale/react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { classes } from '@guest-widgets/shared/src/classes';
import { Feature } from '@guest-widgets/shared/src/features';
import { useFeature } from '@guest-widgets/shared/src/contexts/featureContext/featureContext';
import { useWidget, styled } from '@guest-widgets/core';

import { useReviews } from '../../reviewsContext';
import { useProduct } from '../../productContext';

import { RatingCategories } from './RatingCategories';
import { ReviewList } from './ReviewList';
import { ReviewsDrawer } from './ReviewsDrawer';
import { ReviewsHorizontalPreview } from './ReviewsHorizontalPreview';

const initialReviewsToDisplay = 3;

const Reviews = () => {
  const { value: product } = useProduct().product;
  const { reviews, totalCount, load } = useReviews();
  const { totalVotes } = product;
  const { widgetArea, viewportArea } = useWidget();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { isEnabled } = useFeature();

  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);

  if (!reviews.value || !totalCount || !isEnabled(Feature.RatingAndReview)) return null;

  const preview = reviews.value.slice(0, initialReviewsToDisplay);
  const previewLength = preview.length;

  return (
    <StyledReviews className={classes.details.reviews.root}>
      <Title variant="h2" className={classes.details.reviews.header}>
        <Text id="product-details.reviews.title" length={totalCount} />
      </Title>
      <Box mt={3} className={classes.details.reviews.preview}>
        {totalVotes.length > 0 && (
          <RatingCategoriesWrapper
            className={classes.details.reviews.summary}
            withBorder={viewportArea.isLarge}
          >
            <RatingCategories votes={totalVotes} isStacked={!viewportArea.isLarge} />
          </RatingCategoriesWrapper>
        )}
        {viewportArea.isLarge && (
          <ReviewList reviews={preview} areCategoriesInOneRow={viewportArea.isLarge} />
        )}
        {!viewportArea.isLarge && <ReviewsHorizontalPreview reviews={preview} />}
        {previewLength !== 1 && (totalCount > initialReviewsToDisplay || !viewportArea.isLarge) && (
          <Box
            className={classes.details.reviews.loadButtonContainer}
            mt={3}
            display="flex"
            justifyContent={viewportArea.isLarge ? 'flex-end' : 'flex-start'}
          >
            <Button onClick={openDrawer} variant="outlined" fullWidth={widgetArea.isSmall}>
              <Text id="product-details.reviews.show-all" count={totalCount} />
            </Button>
          </Box>
        )}
      </Box>
      <ReviewsDrawer
        isOpen={isDrawerOpen}
        totalCount={totalCount}
        reviews={reviews}
        onClose={closeDrawer}
        loadMore={load}
      />
    </StyledReviews>
  );
};

const Title = styled(Typography)(({ theme: { typography }, widgetArea }) => ({
  fontSize: widgetArea.basedOnSize(
    typography.h4.fontSize,
    typography.h4.fontSize,
    typography.h2.fontSize
  ),
}));

const StyledReviews = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

type RatingCategoriesWrapperProps = { withBorder: boolean } & React.HTMLAttributes<HTMLDivElement>;

const RatingCategoriesWrapper = muiStyled(
  ({ withBorder, ...props }: RatingCategoriesWrapperProps) => <div {...props} />
)(({ theme: { spacing, border, shape }, withBorder }) => ({
  marginBottom: spacing(3),
  border: withBorder ? border : undefined,
  borderRadius: shape.borderRadius,
  padding: withBorder ? spacing(3, 4) : undefined,
}));

export default Reviews;
